import { ICommand } from './ICommand';
import { CreateShapeCommand } from './CreateShapeCommand';
import { CreateEdgeCommand } from './CreateEdgeCommand';
import { MoveNodesCommand } from './TransformCommands';
import { useDiagramStore } from '../../stores/diagramStore';
import { planQuickCreatePlacement } from '../layout/spatialIntent';

export interface QuickCreateBasicNodeParams {
  sourceNodeId: string;
  variant: string;
  direction: 'top' | 'right' | 'bottom' | 'left';
  sourceX: number;
  sourceY: number;
  sourceW: number;
  sourceH: number;
  category: 'basic' | 'orgchart';
}

/**
 * Quick-create command for basic/org-shape affordance.
 * Creates the new shape and connector as one undoable operation.
 */
export class QuickCreateBasicNodeCommand implements ICommand {
  id: string;
  name = 'Quick Create Basic Node';
  description = 'Create a shape and connect it to the source node';
  timestamp: number;

  private readonly shapeCmd: CreateShapeCommand;
  private readonly edgeCmd: CreateEdgeCommand;
  private readonly moveCmd?: MoveNodesCommand;

  constructor(params: QuickCreateBasicNodeParams) {
    this.timestamp = Date.now();
    this.id = `quick-create-basic-${this.timestamp}`;

    const store = useDiagramStore.getState();
    const newW = params.sourceW;
    const newH = params.sourceH;
    const placement = planQuickCreatePlacement({
      sourceNodeId: params.sourceNodeId,
      nodes: store.nodes,
      edges: store.edges,
      requestedDirection: params.direction,
      newNodeSize: { width: newW, height: newH },
      sourceFallbackRect: {
        x: params.sourceX,
        y: params.sourceY,
        width: params.sourceW,
        height: params.sourceH,
      },
    });
    const createdShapeId = `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const edgeId = `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    this.shapeCmd = new CreateShapeCommand({
      id: createdShapeId,
      type: params.variant,
      category: params.category,
      x: placement.position.x,
      y: placement.position.y,
      width: params.sourceW,
      height: params.sourceH,
    });

    this.edgeCmd = new CreateEdgeCommand({
      id: edgeId,
      type: params.category === 'orgchart' ? 'reports_to' : 'line',
      from: params.sourceNodeId,
      to: createdShapeId,
      category: params.category === 'orgchart' ? 'org' : 'basic',
      fromAnchor: placement.fromAnchor,
      toAnchor: placement.toAnchor,
      fromOffset: 0.5,
      toOffset: 0.5,
      attrs: {
        routingType: params.category === 'orgchart' ? 'orthogonalRounded' : 'orthogonal',
      },
    });

    if (placement.moves.length > 0) {
      this.moveCmd = new MoveNodesCommand(placement.moves);
    }
  }

  execute(): void {
    this.shapeCmd.execute();
    if (!this.shapeCmd.getCreatedId()) return;
    this.edgeCmd.execute();
    this.moveCmd?.execute();
  }

  undo(): void {
    this.moveCmd?.undo();
    this.edgeCmd.undo();
    this.shapeCmd.undo();
  }

  redo(): void {
    this.shapeCmd.redo();
    this.edgeCmd.redo();
    this.moveCmd?.redo();
  }
}
